/* eslint-disable camelcase */
import qs from 'qs';
import {
  CONSOLIDATED_METHODS,
  MULTI_ORDER_RETURN_METHODS,
  RETURN_METHODS,
} from '../../constants/endpoints';
import generateMethodKey from '../../lib/generateMethodKey';
import {
  ENABLE_AVAILABLE_OPTIONS_API_FLOW,
  receiveApiReturnOptions,
  receiveConsolidatedMethods,
  receiveReturnProhibited,
} from '../returnOptions';
import { modifyUrlParams, getUrlInfo } from '../../lib/url';
import { mapCostsFromWholeToDecimalUnits } from '../../lib/currencyFormatters';
import { getOrderWithMultiPayload } from '../../react-components/OrderSubmitButtons/utils';
import { hasMultiOrder } from '../../react-components/AdditionalOrderForm/utils';

export const SELECT_RETURN_METHOD = 'SELECT_RETURN_METHOD';
export const RECEIVE_RETURN_METHODS = 'RECEIVE_RETURN_METHODS';

export const RESET_RETURN_METHOD = 'RESET_RETURN_METHOD';

export const selectReturnMethod = ({ selectedMethod, selectedMethodKey }) => ({
  type: SELECT_RETURN_METHOD,
  data: {
    selectedMethod,
    selectedMethodKey,
  },
});

export const resetReturnMethod = () => ({
  type: RESET_RETURN_METHOD,
});

export const fetchReturnMethods =
  ({ returnOrder }) =>
  async (dispatch, getState) => {
    try {
      const state = getState();
      const {
        page: {
          locale,
          productType,
          giftOrder = false,
          browserFingerprint,
          overrideCode,
          deferOverride,
        } = {},
        retailerSettings: { name: retailerName } = {},
        returnsSettings: {
          [productType]: {
            return_methods: settingsMethods = {},
            returnOptions: { enabled: returnOptionsEnabled } = {},
          } = {},
        } = {},
      } = state;

      const response = await fetch(RETURN_METHODS({ retailerName, locale }), {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'narvar-tfid': browserFingerprint,
        },
        body: JSON.stringify({
          order_number: returnOrder.order_number,
          return_items: returnOrder.return_items,
          customer_id: returnOrder.customer_id,
          ...(deferOverride ? {} : { return_override_code: overrideCode }),
          gift: giftOrder,
          product_type: productType,
          return_method: '', // For some reason backend needs this...
        }),
      });

      if (response.status === 302 || response.status === 403) {
        modifyUrlParams({
          paramsToDelete: ['init'],
          paramsToAppend: [['expired', 'true']],
        });
        return window.location.reload();
      }

      const { errors, return_methods: apiMethods = null } = await response.json();

      if (!apiMethods) throw new Error(errors);

      if (returnOptionsEnabled) {
        return dispatch(receiveApiReturnOptions(apiMethods));
      }

      const returnMethods = settingsMethods
        .filter(({ enabled }) => enabled)
        .reduce((matchedMethods, settingsMethod) => {
          const apiMatch = apiMethods.find(
            (apiMethod) => generateMethodKey(apiMethod) === generateMethodKey(settingsMethod),
          );

          return apiMatch
            ? [
                ...matchedMethods,
                {
                  ...settingsMethod,
                  ...apiMatch,
                },
              ]
            : matchedMethods;
        }, []);

      return dispatch({ type: RECEIVE_RETURN_METHODS, data: { returnMethods } });
    } catch (error) {
      return console.error(error); // eslint-disable-line
    }
  };

export const fetchConsolidatedMethods =
  ({ returnOrder }) =>
  async (dispatch, getState) => {
    try {
      const {
        page,
        page: {
          locale,
          giftOrder = false,
          browserFingerprint,
          overrideCode,
          initialPageLoadParams,
          deferOverride,
          countryCode,
          productType,
        } = {},
        retailerSettings: { name: retailerName } = {},
        storeLocator: { postalCode, country } = {},
        multiOrders,
        landingPage: { landingForm } = {},
        returnsSettings: { [productType]: { isSimpleReturnsEnhanced = false, validation: { returnType } = {} } = {} } = {},
      } = getState();

      dispatch({ type: ENABLE_AVAILABLE_OPTIONS_API_FLOW });

      const { params: { preview, version } = {} } = getUrlInfo();

      const hasMultiOrders = hasMultiOrder({ returnItems: returnOrder.return_items });

      let body = {
        order_number: returnOrder.order_number,
        return_items: returnOrder.return_items,
        postal_code: postalCode,
        country,
        ...(deferOverride ? {} : { return_override_code: overrideCode }),
        gift: giftOrder,
      };

      if (hasMultiOrders) {
        const {
          primary_order_number,
          simple_return,
          gift_return,
          locale: orderLocale,
          orders,
        } = getOrderWithMultiPayload({
          page,
          returnItems: returnOrder.return_items,
          confirmOrder: returnOrder,
          multiOrders,
        });

        body = {
          primary_order_number,
          simple_return,
          gift_return,
          locale: orderLocale,
          number_of_locations: 10,
          postal_code: postalCode,
          country_code: country,
          orders,
        };
      }

      const params = qs.stringify({
        locale,
        ...(preview === 'true' ? { preview, settings_version: version } : {}),
        ...(initialPageLoadParams?.mode ? { mode: initialPageLoadParams?.mode } : {}),
      });

      if (returnType === 'simple_returns' && isSimpleReturnsEnhanced) {
        body = {
          ...body,
          simple_return: true,
          country: countryCode,
          ...landingForm,
        };
      }

      const response = await fetch(
        hasMultiOrders
          ? MULTI_ORDER_RETURN_METHODS({
              retailerName,
              params,
            })
          : CONSOLIDATED_METHODS({
              retailerName,
              params,
            }),
        {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'narvar-tfid': browserFingerprint,
          },
          body: JSON.stringify(body),
        },
      );

      if (response.status === 302 || response.status === 403) {
        modifyUrlParams({
          paramsToDelete: ['init'],
          paramsToAppend: [['expired', 'true']],
        });
        return window.location.reload();
      }

      const responseBody = await response.json();

      if (responseBody.errors && responseBody.errors.some(error => error?.message === 'return_prohibited')) {
        dispatch(receiveReturnProhibited());
        return responseBody;
      }

      const {
        location_based_pudo_options: pudoOptionsWithLocations = [],
        no_location_based_pudo_options: pudoOptionsNoLocations = [],
      } = responseBody;
      const consolidatedMethods = [
        ...pudoOptionsWithLocations.map((option) => ({
          ...option,
          locations: option.locations.map((location) => ({
            ...location,
            ...(location.address
              ? {
                  address: {
                    address_line1: location.address.address1,
                    address_line2: location.address.address2,
                    city: location.address.city,
                    country: location.address.country,
                    country_code: location.address.country_code,
                    postal_code: location.address.postal_code,
                    region: location.address.region,
                    region_code: location.address.region_code,
                  },
                  store_address: {
                    address_line1: location.address.address1,
                    address_line2: location.address.address2,
                    city: location.address.city,
                    country: location.address.country,
                    country_code: location.address.country_code,
                    postal_code: location.address.postal_code,
                    region: location.address.region,
                    region_code: location.address.region_code,
                  },
                }
              : undefined),
            logos: {
              square_1x: { url: location.logo_url },
              square_2x: { url: location.logo_url },
            },
            location_id: location.id,
          })),
          return_cost: mapCostsFromWholeToDecimalUnits(option.return_cost),
        })),
        ...pudoOptionsNoLocations.map((option) => ({
          ...option,
          return_cost: mapCostsFromWholeToDecimalUnits(option.return_cost),
        })),
      ];
      dispatch(receiveConsolidatedMethods(consolidatedMethods));
      return responseBody;
    } catch (error) {
      return console.error(error);
    }
  };
